import { useCallback } from 'react';
import axios from 'axios';
import apiConfig from '../../../constants/apiConfig';
import { useSignedUrl } from '../../FileManagement/hooks/useNotebookQueries';

interface FileUrlResult {
  url: string;
  fileName: string;
  mimetype: string;
}

/**
 * Custom hook to fetch file data and generate signed URL for citation clicks
 *
 * @returns Function that takes fileId and returns { url, fileName, mimetype }
 */
export function useFetchCitationFileUrl() {
  const signedUrlMutation = useSignedUrl();

  const handleFetchFileUrl = useCallback(
    async (fileId: string): Promise<FileUrlResult> => {
      try {
        // GET /dice/files/folders/:id returns { parent, child }; the
        // resource the caller asked for is in `parent` (the legacy controller
        // treats files and folders identically by id).
        const response = await axios.get<{
          parent: Array<{
            fileUrl?: string;
            file_name?: string;
            mimetype?: string;
          }>;
          child: unknown[];
        }>(`${apiConfig.apiGateway.foldersBase}/${fileId}`);

        const parentData = response?.data?.parent;
        if (
          !parentData ||
          !Array.isArray(parentData) ||
          parentData.length === 0
        ) {
          throw new Error('File not found');
        }

        const fileData = parentData[0];
        if (!fileData || !fileData.fileUrl) {
          throw new Error('File missing URL');
        }

        // Generate signed URL
        const signedUrl = await signedUrlMutation.mutateAsync(fileData.fileUrl);

        return {
          url: signedUrl,
          fileName: fileData.file_name || 'Unknown File',
          mimetype: fileData.mimetype || 'application/octet-stream',
        };
      } catch (error) {
        console.error('Error fetching file for citation:', error);
        throw error;
      }
    },
    [signedUrlMutation]
  );

  return handleFetchFileUrl;
}
